import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useQuery } from '@tanstack/react-query';
import * as artifactService from '../../services/artifactService';
import { LineageRelationType } from '../../types/lineage';
import ArtifactNode from '../graphs/NodeTypes/ArtifactNode';
import { TimelineControl } from './TimelineControl';

interface LineageViewerProps {
  artifactId: string;
  height?: string;
  onNodeClick?: (artifactId: string) => void;
  viewMode?: 'full' | 'ancestry' | 'descendants';
}

const nodeTypes = {
  artifact: ArtifactNode
};

// Map relationship types to visual properties
const relationshipTypeConfig: Record<LineageRelationType, {
  label: string;
  color: string;
  strokeWidth: number;
  markerEnd: { type: MarkerType; color: string };
}> = {
  [LineageRelationType.DERIVED_FROM]: {
    label: 'Derived From',
    color: '#3b82f6', // blue
    strokeWidth: 2,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' }
  },
  [LineageRelationType.INSPIRED_BY]: {
    label: 'Inspired By',
    color: '#f59e0b', // amber
    strokeWidth: 1.5,
    markerEnd: { type: MarkerType.Arrow, color: '#f59e0b' }
  },
  [LineageRelationType.CRITIQUE_OF]: {
    label: 'Critique Of',
    color: '#ef4444', // red
    strokeWidth: 1.5,
    markerEnd: { type: MarkerType.Arrow, color: '#ef4444' }
  },
  [LineageRelationType.IMPLEMENTATION_OF]: {
    label: 'Implements',
    color: '#8b5cf6', // purple
    strokeWidth: 2,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#8b5cf6' }
  },
  [LineageRelationType.REFINEMENT_OF]: {
    label: 'Refines',
    color: '#10b981', // green
    strokeWidth: 2,
    markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' }
  },
  [LineageRelationType.ALTERNATIVE_TO]: {
    label: 'Alternative To',
    color: '#6b7280', // gray
    strokeWidth: 1,
    markerEnd: { type: MarkerType.Arrow, color: '#6b7280' }
  }
};

export const LineageViewer: React.FC<LineageViewerProps> = ({
  artifactId,
  height = '600px',
  onNodeClick,
  viewMode = 'full'
}) => {
  const { darkMode } = useTheme();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [maxDepth, setMaxDepth] = useState(3);
  const [relationshipTypes, setRelationshipTypes] = useState<LineageRelationType[]>(
    Object.values(LineageRelationType)
  );
  const [timeRange, setTimeRange] = useState<[Date | null, Date | null]>([null, null]);
  const [layoutMode, setLayoutMode] = useState<'timeline' | 'tree' | 'force'>('timeline');

  // Determine which API endpoint to use based on viewMode
  const getLineageData = () => {
    switch (viewMode) {
      case 'ancestry':
        return artifactService.getArtifactAncestry(artifactId, maxDepth, relationshipTypes);
      case 'descendants':
        return artifactService.getArtifactDescendants(artifactId, maxDepth, relationshipTypes);
      default:
        return artifactService.getArtifactLineage(artifactId, maxDepth, relationshipTypes);
    }
  };

  const { data: lineageData, isLoading, error } = useQuery(
    ['artifactLineage', artifactId, maxDepth, relationshipTypes, viewMode],
    getLineageData,
    { enabled: !!artifactId }
  );

  useEffect(() => {
    if (lineageData) {
      // Get min and max dates for timeline
      const dates = lineageData.nodes.map((node: any) => new Date(node.created_at));
      const minDate = new Date(Math.min(...dates.map((d: Date) => d.getTime())));
      const maxDate = new Date(Math.max(...dates.map((d: Date) => d.getTime())));
      
      if (!timeRange[0] || !timeRange[1]) {
        setTimeRange([minDate, maxDate]);
      }

      // Filter nodes by time range if set
      const filteredNodes = timeRange[0] && timeRange[1] 
        ? lineageData.nodes.filter((node: any) => {
            const nodeDate = new Date(node.created_at);
            return nodeDate >= timeRange[0]! && nodeDate <= timeRange[1]!;
          })
        : lineageData.nodes;
      
      // Get IDs of filtered nodes
      const filteredNodeIds = new Set(filteredNodes.map((node: any) => node.id));
      
      // Filter edges to only include connections between filtered nodes
      const filteredEdges = lineageData.edges.filter(
        (edge: any) => filteredNodeIds.has(edge.source_id) && filteredNodeIds.has(edge.target_id)
      );

      // Calculate positions based on layout mode
      let positions: Record<string, { x: number, y: number }> = {};
      
      if (layoutMode === 'timeline') {
        // Timeline layout: x-axis is time, y-axis is grouped by type
        const typeGroups: Record<string, number> = {};
        const timeSpan = maxDate.getTime() - minDate.getTime();
        const width = 1200; // arbitrary width
        
        filteredNodes.forEach((node: any) => {
          const nodeType = node.type;
          if (!typeGroups[nodeType]) {
            typeGroups[nodeType] = Object.keys(typeGroups).length * 100 + 50;
          }
          
          const nodeDate = new Date(node.created_at);
          const x = ((nodeDate.getTime() - minDate.getTime()) / timeSpan) * width;
          const y = typeGroups[nodeType];
          
          positions[node.id] = { x, y };
        });
      } else if (layoutMode === 'tree') {
        // Tree layout: root at top, descendants below
        // This is a simplified tree layout - a real implementation would be more complex
        const levels: Record<string, number> = {};
        const visited = new Set<string>();
        
        // Helper function to calculate levels
        const calculateLevels = (nodeId: string, level: number) => {
          if (visited.has(nodeId)) return;
          visited.add(nodeId);
          levels[nodeId] = Math.max(level, levels[nodeId] || 0);
          
          // Find children
          filteredEdges
            .filter((edge: any) => edge.source_id === nodeId)
            .forEach((edge: any) => calculateLevels(edge.target_id, level + 1));
          
          // Find parents (for bidirectional traversal)
          filteredEdges
            .filter((edge: any) => edge.target_id === nodeId)
            .forEach((edge: any) => calculateLevels(edge.source_id, level - 1));
        };
        
        // Start from the root
        calculateLevels(lineageData.root_id, 0);
        
        // Calculate x positions by grouping nodes at the same level
        const nodesAtLevel: Record<number, string[]> = {};
        Object.entries(levels).forEach(([nodeId, level]) => {
          if (!nodesAtLevel[level]) nodesAtLevel[level] = [];
          nodesAtLevel[level].push(nodeId);
        });
        
        // Position nodes
        Object.entries(levels).forEach(([nodeId, level]) => {
          const nodesInLevel = nodesAtLevel[level].length;
          const index = nodesAtLevel[level].indexOf(nodeId);
          const x = (index + 1) * (1200 / (nodesInLevel + 1));
          const y = level * 100 + 50;
          
          positions[nodeId] = { x, y };
        });
      } else {
        // Force layout would be implemented with a force-directed algorithm
        // For simplicity, we'll use a random layout here
        filteredNodes.forEach((node: any) => {
          positions[node.id] = { 
            x: Math.random() * 800 + 100, 
            y: Math.random() * 500 + 50 
          };
        });
      }

      // Transform the lineage data into ReactFlow nodes and edges
      const flowNodes: Node[] = filteredNodes.map((node: any) => ({
        id: node.id,
        type: 'artifact',
        position: positions[node.id] || { x: 0, y: 0 },
        data: {
          label: node.name,
          type: node.type,
          creator: node.agent_name,
          version: node.version
        },
        // Highlight the current artifact
        style: node.artifact_id === artifactId ? {
          boxShadow: '0 0 10px #3b82f6',
          border: '2px solid #3b82f6'
        } : undefined
      }));

      const flowEdges: Edge[] = filteredEdges.map((edge: any) => {
        const config = relationshipTypeConfig[edge.type as LineageRelationType] || {
          label: edge.type,
          color: '#888',
          strokeWidth: 1,
          markerEnd: { type: MarkerType.Arrow, color: '#888' }
        };

        return {
          id: edge.id,
          source: edge.source_id,
          target: edge.target_id,
          label: config.label,
          type: 'smoothstep',
          animated: edge.type === LineageRelationType.DERIVED_FROM,
          style: { stroke: config.color, strokeWidth: config.strokeWidth },
          markerEnd: config.markerEnd
        };
      });

      setNodes(flowNodes);
      setEdges(flowEdges);
    }
  }, [lineageData, setNodes, setEdges, artifactId, layoutMode, timeRange]);

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (onNodeClick) {
        // Extract the artifact ID from the node data
        const nodeData = lineageData?.nodes.find((n: any) => n.id === node.id);
        if (nodeData) {
          onNodeClick(nodeData.artifact_id);
        }
      }
    },
    [onNodeClick, lineageData]
  );

  const handleRelationshipTypeChange = (type: LineageRelationType) => {
    setRelationshipTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  if (isLoading) {
    return (
      <div className={`flex justify-center items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{ height }}>
        Loading lineage data...
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center rounded-lg p-4 ${
        darkMode ? 'bg-red-900/20 border border-red-700 text-red-400' : 'text-red-500'
      }`} style={{ height }}>
        Error loading lineage data
      </div>
    );
  }

  if (!isLoading && !error && (!lineageData || lineageData.nodes.length === 0)) {
    return (
      <div className={`flex justify-center items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} style={{ height }}>
        No lineage data found for this artifact.
      </div>
    );
  }

  return (
    <div className="lineage-viewer">
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div>
          <label className={`mr-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Depth:</label>
          <select
            value={maxDepth}
            onChange={e => setMaxDepth(Number(e.target.value))}
            className={`border rounded px-2 py-1 text-sm ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value={1}>1 Level</option>
            <option value={2}>2 Levels</option>
            <option value={3}>3 Levels</option>
            <option value={5}>5 Levels</option>
            <option value={10}>10 Levels</option>
          </select>
        </div>

        <div>
          <label className={`mr-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Layout:</label>
          <select
            value={layoutMode}
            onChange={e => setLayoutMode(e.target.value as any)}
            className={`border rounded px-2 py-1 text-sm ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="timeline">Timeline</option>
            <option value="tree">Tree</option>
            <option value="force">Force-Directed</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-1">
          {Object.entries(relationshipTypeConfig).map(([type, config]) => (
            <button
              key={type}
              className={`px-2 py-1 text-xs rounded-full flex items-center ${
                relationshipTypes.includes(type as LineageRelationType)
                  ? darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-800'
                  : darkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'
              }`}
              onClick={() => handleRelationshipTypeChange(type as LineageRelationType)}
              style={{ borderColor: config.color, borderWidth: '1px', borderStyle: 'solid' }}
            >
              <span className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: config.color }} />
              {config.label}
            </button>
          ))}
        </div>
      </div>

      {lineageData && timeRange[0] && timeRange[1] && (
        <div className={`mb-4 border rounded-lg p-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <TimelineControl
            startDate={new Date(Math.min(...lineageData.nodes.map((n: any) => new Date(n.created_at).getTime())))}
            endDate={new Date(Math.max(...lineageData.nodes.map((n: any) => new Date(n.created_at).getTime())))}
            selectedRange={timeRange}
            onRangeChange={setTimeRange}
          />
        </div>
      )}

      <div style={{ height }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          nodeTypes={nodeTypes}
          fitView
        >
          <Controls />
          <MiniMap style={{ background: darkMode ? '#1f2937' : '#f9fafb' }} />
          <Background color={darkMode ? '#374151' : '#aaaaaa'} />
        </ReactFlow>
      </div>
    </div>
  );
};
